import { css } from '@emotion/react';
import { almostWhite } from 'athena/styles/colors';
import { largeSpacing, standardSpacing, tripleSpacing } from 'styles/global_defaults/scaffolding';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AngularContext, AngularServicesContext } from 'react-app';
import { CompletionStatus, MentoringProgramEnrollment, StepKey } from 'redux/schemas/models/mentoring-program-enrollments';
import { getSanitizedStyles } from 'shared/utils';
import { white } from 'styles/global_defaults/colors';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { useAppDispatch } from 'redux/store';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import t from 'react-translate';
import { getCalendarIntegrationSettings, getMentorshipProgramSession } from 'redux/selectors/mentorship-program-sessions';
import { IntegrationAuth, SessionFilters, translationIntegrationKeys } from 'redux/schemas/models/mentoring-program-sessions';
import { SessionTypes } from 'redux/schemas/models/live-event';
import _ from 'lodash';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import { HomeProps } from '../types';
import { withOverflowFlexGrowClass } from '../../constants';
import ProgramOrientation from './program-orientation';
import ConnectionDetails from './connection-details';
import MatchCelebration from './match-celebration';
import StepList from './step-list';
import SessionsList from './sessions-list';
import UnreleasedFeature from './unreleased-feature';
import ProgramClosed from './program-closed';
import PlanSessionFlyoutModal from './plan-session-flyout-modal';
import MentoringProgramContext from '../../context';
import ActionItemsPanel from './action-items-panel';
import ProfileModal from './step-list/profile-completion/profile-modal';
import PulseCheckModal from './next-steps-panel/session-pulse-check/pulse-check-modal';
import NextStepsPanel from './next-steps-panel';
import { getOAuthLink, isTokenExpired } from './session-edit-mode/utils';
import EnableIntegrationsModal from './session-edit-mode/virtual-meeting/enable-integrations-modal';
import GoalsList from './goals-list';
import { wrapThunkAction } from 'redux/utils';
import { getCalendarIntegrationStatus } from 'redux/actions/mentoring-program-enrollments';
import useCalendarIntegration from 'athena/hooks/use-calendar-integration';
import MentorRequestModal from './mentor-request-modal/mentor-request-modal';
import GuideModal from './guide-modal';
import { getMentorshipProgramGuides } from 'redux/actions/mentoring-program-guides';
import { getMentoringProgramGuideById } from 'redux/selectors/mentorship-program-guides';
import { getMentoringProgramSessions } from 'redux/actions/mentoring-program-sessions';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import ReflectionWorkspaceContainer from './reflection-workspace-container';
import { selectMentoringProgramReflectionParticipantResponses } from 'redux/selectors/mentoring-program-reflections';
import { getMentoringProgramReflectionResponses } from 'redux/actions/mentoring-program-reflections';
import ProgramReflectionBanner from './program-reflection-banner';
import { screenMobileMin } from 'styles/global_defaults/media-queries';
import MenteeLensPanel from './mentee-lens-panel';
import useMentoringProgramRole from 'athena/hooks/use-mentoring-program-role';

export const LocalStorageKeys = {
  redirection: 'isRedirectedAfterOAuth',
  session: 'editingSession',
  [SessionTypes.ZOOM]: 'expiredMessageDisplayedZoom',
  [SessionTypes.MS_TEAMS]: 'expiredMessageDisplayedMsTeams',
  [SessionTypes.MS_CALENDAR]: 'expiredMessageDisplayedMsCalendar',
};

export enum AuthenticationView {
  SHOW_SESSION = 1,
  SHOW_INTEGRATIONS = 2,
  CREATE_MEETING_PLAN = 3,
  SHOW_MEETING_PLAN_INTEGRATIONS = 4,
}

type ExpiredToken = {
  sessionType: SessionTypes;
  email: string;
};

const withOverflowFlexGrowStyles = css`
  .${withOverflowFlexGrowClass} {
    flex: 1;
    min-height: 0;
  }
`;

const unreleasedStyles = (containerTranslate: number) => css`
  padding: 0;
  background-color: ${almostWhite};
  justify-content: center;
  .unreleased-section {
    width: 520px;
    max-height: 540px;
    height: calc(100% + ${120 - containerTranslate}px);
    transform: translateY(-${250 - containerTranslate}px);
    ${withOverflowFlexGrowStyles};
    background-color: ${white};
    border-radius: 10px;
    .description {
      max-width: 350px;
      text-align: center;
    }
  }
}`;

const styles = (
  containerTranslate: number,
  isTablet: boolean,
  isUnauthorized: boolean,
  showProgramReflectionBanner: boolean,
) => css`
  display: grid;
  align-items: center;
  min-height: ${isUnauthorized ? 'calc(100% - 150px)' : '100%'};
  height: fit-content;
  background-color: ${almostWhite};
  padding: 0 ${standardSpacing}px;
  ${isTablet && css`
    padding-bottom: ${tripleSpacing}px;
  `};

  .participant-home {
    display: grid;
    gap: ${standardSpacing}px;
    grid-template-columns: auto 380px;
    height: calc(100% + ${120 - containerTranslate}px);
    transform: translateY(-${120 - containerTranslate}px);
    .right-panel.panel-container {
      ${!isTablet && showProgramReflectionBanner && css`
        margin-top: -100px;
      `}
    }
    ${withOverflowFlexGrowStyles};
    @media(max-width: 834px) {
      grid-template-columns: 1fr;
      .right-panel {
        .info-container {
          > img {
            width: 100%;
            ${isTablet && css`
              height: 120px;
              object-fit: cover;
            `};
          }
        }
      }
    }

    .left-panel, .right-panel {
      gap: ${standardSpacing}px;
    }
    @media(max-width: ${screenMobileMin}px) {
      .left-panel, .panel-container {
        width: calc(100% - ${largeSpacing}px);
      }
    }
  }
`;

const getSanitizedIntegrations = (allIntegrations: IntegrationAuth) => {
  const sanitizedIntegrations = { ...allIntegrations };
  const sanitizedSource = sanitizedIntegrations.source.filter((integration) => (
    integration !== 'zoom_account_oauth'
  ));
  sanitizedIntegrations.source = sanitizedSource;
  delete (sanitizedIntegrations as any)?.zoomAccountOauth;
  return sanitizedIntegrations;
};

const ParticipantHome = ({ scrollPercentage }: HomeProps) => {
  const containerTranslate = (90 / 100) * scrollPercentage;

  const [showPlanSessionFlyoutModal, setShowPlanSessionFlyoutModal] = useState(false);
  const [sessionToEdit, setSessionToEdit] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const [tokenExpired, setTokenExpired] = useState<boolean>(false);
  const [expiredAccounts, setExpiredAccounts] = useState<ExpiredToken[]>(null);
  const dispatch = useAppDispatch();

  const openPlanSessionFlyout = (session, editMode = false, createMeetingPlan = false) => {
    setSessionToEdit(session);
    setShowPlanSessionFlyoutModal(true);
    setEditMode(editMode);
    setCreateMeetingPlan(createMeetingPlan);
  };

  const { injectServices } = useContext(AngularContext);
  const { $location } = useContext(AngularServicesContext);
  const [$state, InstitutionsManager, AlertMessages] = injectServices(['$state', 'InstitutionsManager', 'AlertMessages']);
  const connectionId = $state.params.connectionId ? parseInt($state.params.connectionId, 10) : null;
  const { institution = {} } = InstitutionsManager;
  const { liveSessionSettings = {} } = institution;
  const allIntegrations: IntegrationAuth = liveSessionSettings || {};
  const integrations: IntegrationAuth = useMemo(() => (
    allIntegrations?.source?.length > 0 ? getSanitizedIntegrations(allIntegrations) : null
  ), [allIntegrations]);

  const {
    showProfile = false,
    authenticationView = null,
    showSession = null,
    sessionId = null,
    newSession = false,
    guideId = null,
    source = null,
    mentorRequest = null,
    mentorId = null,
    showReflections = false,
  } = $state.params || {};
  const showMentorRequestModal = !!source && (!!mentorRequest || !!mentorId);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(showProfile);
  const [createMeetingPlan, setCreateMeetingPlan] = useState((
    authenticationView === AuthenticationView.CREATE_MEETING_PLAN
    || authenticationView === AuthenticationView.SHOW_MEETING_PLAN_INTEGRATIONS
  ));
  const sessionFromState = useSelector(getMentorshipProgramSession(showSession)) || {};
  const [showPulseCheckModal, setShowPulseCheckModal] = useState<boolean>(
    !!sessionId,
  );
  const [showIntegrationsModal, setShowIntegrationsModal] = useState<boolean>((
    authenticationView === AuthenticationView.SHOW_INTEGRATIONS
    || authenticationView === AuthenticationView.SHOW_MEETING_PLAN_INTEGRATIONS
  ));
  const mentoringProgramGuide = useSelector(getMentoringProgramGuideById(guideId));
  const { hasAiComponentGeneration } = useSelector(getCurrentInstitution) || {};

  const openSessionPulseCheck = (sessionId: number) => {
    $state.go('mentoring-program-single-connection', { connectionId, sessionId });
    setShowPulseCheckModal(true);
  };

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id: mentoringProgramId,
    endDate,
    releasedDate,
    mentorshipProgramEnrollment,
    matchAnnouncementDate,
    pulseChecksEnabled,
    guidanceEnabled,
    aiMeetingPrepEnabled,
    matchingType,
    programReflectionEnabled,
  } = mentoringProgram || {};
  const isFeatureReleased = releasedDate && new Date() > new Date(releasedDate);
  const isProgramEnded = endDate && new Date() > new Date(endDate);
  const announcementDateHasPassed = moment().isAfter(moment(matchAnnouncementDate));

  const unreleasedFeatureStyles = (isFeatureReleased && !isProgramEnded)
    ? ''
    : unreleasedStyles(containerTranslate);

  const { isMentor } = useMentoringProgramRole();

  const mentoringProgramEnrollment: MentoringProgramEnrollment = useSelector(state => (
    state.models.mentoringProgramEnrollments[mentorshipProgramEnrollment]
  ));
  const { assignedConnections, states: enrollmentStates, id: enrollmentId } = mentoringProgramEnrollment;
  const hasSingleConnection = assignedConnections?.length === 1;

  const connection = assignedConnections?.find((connec) => (
    connec.connectionId === connectionId
  )) ?? assignedConnections?.[0];
  const { isSessionCreated, states: connectionStates } = connection || {};
  const states = useMemo(
    () => ({ ...enrollmentStates, ...connectionStates }),
    [enrollmentStates, connectionStates],
  );

  const calendarIntegrationSettings = useSelector(getCalendarIntegrationSettings(mentoringProgramId, enrollmentId));
  const { hasMs365CalendarIntegration } = useCalendarIntegration();
  const connectionCalendarIntegrationSettings = useSelector(
    getCalendarIntegrationSettings(mentoringProgramId, connection?.assignedEnrollment?.id),
  );
  const [shouldShowPulseCheckCTA, setShouldShowPulseCheckCTA] = useState(false);

  useEffect(() => {
    const isPastSession = moment().isAfter(
      moment(sessionToEdit?.scheduledDate).add(sessionToEdit?.duration || 0, 'minutes'),
    );
    const isPulseCheckResponded = sessionToEdit?.pulseCheckResponses?.[mentorshipProgramEnrollment];
    setShouldShowPulseCheckCTA(pulseChecksEnabled && isPastSession && !isPulseCheckResponded);
  }, [sessionToEdit]);

  const isTablet = useMediaQuery({
    query: '(max-width: 834px)',
  });
  const isParticipantDrivenMatching = matchingType === MentoringProgramMatchingType.PARTICIPANT_DRIVEN;

  const shouldShowConnectionContent = connection && (announcementDateHasPassed || isParticipantDrivenMatching);
  const actionItems = shouldShowConnectionContent && (
    <ActionItemsPanel
      enrollment={mentoringProgramEnrollment}
      connection={connection}
      showPlanSessionFlyout={openPlanSessionFlyout}
    />
  );

  useEffect(() => {
    if (newSession) {
      openPlanSessionFlyout(null);
    }
  }, [newSession]);

  useEffect(() => {
    if (!connectionId) {
      const id = assignedConnections?.[0]?.connectionId;
      if (id) {
        $state.go(
          'mentoring-program-single-connection',
          { connectionId: id },
          { reload: false },
        );
      }
    } else if (
      !connectionCalendarIntegrationSettings
      && !!connection?.assignedEnrollment?.id
      && hasMs365CalendarIntegration
    ) {
      wrapThunkAction(
        dispatch(
          getCalendarIntegrationStatus({
            programId: mentoringProgramId,
            enrollmentId: connection?.assignedEnrollment?.id,
          }),
        ),
      );
    }
  }, [connectionId]);

  const checkCompletedState = Object.entries(states).every(([key, value]) => {
    const { status } = value;
    if ([StepKey.WAITING_CONNECTION, StepKey.WAITING_FIRST_SESSION].includes(key as StepKey)) {
      return (status === CompletionStatus.COMPLETED) && isSessionCreated;
    }
    return true;
  });
  const checkWaitingSessionCompleted = states[StepKey.WAITING_FIRST_SESSION]?.status === CompletionStatus.COMPLETED && isSessionCreated;
  const [areAllStepsCompleted, setAreAllStepsCompleted] = useState(checkCompletedState);
  const [isWaitingFirstSessionCompleted, setIsWaitingFirstSessionCompleted] = useState(checkWaitingSessionCompleted);

  useEffect(() => {
    if (checkCompletedState) {
      setTimeout(() => {
        setAreAllStepsCompleted(true);
      }, 5000);
    }
    if (checkWaitingSessionCompleted) {
      setTimeout(() => {
        setIsWaitingFirstSessionCompleted(true);
      }, 5000);
    }
  }, [checkCompletedState, checkWaitingSessionCompleted]);

  const resetNewSessionParam = () => {
    const params = { newSession: undefined };
    $state.go($state.current, params, { notify: false });
  };


  const participantResponses = useSelector((state) => selectMentoringProgramReflectionParticipantResponses(state, connectionId));
  const currentEnrollmentResponses = useMemo(
    () => participantResponses?.data?.find(r => r.mentorshipProgramEnrollmentId === enrollmentId) ?? null,
    [participantResponses, enrollmentId],
  );

  const isRedirected = localStorage.getItem(LocalStorageKeys.redirection) || false;
  const itemFromSessionStorage = localStorage.getItem(LocalStorageKeys.session);
  const sessionFromStorage = JSON.parse(itemFromSessionStorage !== 'undefined' ? itemFromSessionStorage : null);

  const editModeRef = useRef(null);

  const [showProgramReflectionBanner, setShowProgramReflectionBanner] = useState(false);

  useEffect(() => {
    const isReflectionCompleted = currentEnrollmentResponses?.answers?.length > 0;
    setShowProgramReflectionBanner(programReflectionEnabled && isReflectionCompleted && !showReflections);
  }, [programReflectionEnabled, currentEnrollmentResponses, showReflections]);

  useEffect(() => {
    const showAuthenticationView = authenticationView && (sessionFromState?.id || !showSession) && isRedirected;
    const showSessionViewMode = showSession >= 0 && !authenticationView && sessionFromState?.id;
    if (showAuthenticationView || showSessionViewMode) {
      const session = _.merge(sessionFromState, sessionFromStorage);
      const editMode = !showSessionViewMode;
      openPlanSessionFlyout(session, editMode, createMeetingPlan);
      if (authenticationView) {
        dispatch(
          addAlertMessage({
            type: AlertMessageType.SUCCESS,
            header: t.FORM.SUCCESS_BANG(),
            message: t.MENTORING_PROGRAMS.PARTICIPANT_HOME.PLAN_SESSION_FLYOUT.VIRTUAL_MEETING.SUCCESS_MESSAGE(),
          }),
        );
        localStorage.removeItem(LocalStorageKeys.redirection);
        localStorage.removeItem(LocalStorageKeys.session);
      }
    }
  }, [authenticationView, showSession, sessionFromState?.id]);

  useEffect(() => {
    if (integrations) {
      const tokenExpired = isTokenExpired(integrations, calendarIntegrationSettings);
      setExpiredAccounts(tokenExpired);
      setTokenExpired(!!tokenExpired.length);
    }
  }, [integrations, calendarIntegrationSettings]);

  useEffect(() => {
    if (tokenExpired) {
      expiredAccounts.forEach((account) => {
        const localStorageKey = LocalStorageKeys[account.sessionType] || '';
        const wasExpiredMessageDisplayed = localStorage.getItem(localStorageKey);
        if (wasExpiredMessageDisplayed === 'true') {
          return;
        }
        const { sessionType, email } = account || {};
        const baseText = 'MENTORING_PROGRAMS.PARTICIPANT_HOME.EXPIRED_TOKEN';

        const refererUrl = `${$location.protocol()}://${$location.host()}/#!${$location.path()}${$location.hash()}`;
        const href = getOAuthLink({ sessionType, mentoringProgramId, enrollmentId, refererUrl });
        const button = { text: `${baseText}.BUTTON`, href };

        const message = `${baseText}.${translationIntegrationKeys[sessionType]}`;
        const params = ['', message, {}, { email }, Infinity, button];

        AlertMessages.oAuthErrorForMentoringPrograms(...params);
        localStorage.setItem(localStorageKey, 'true');
      });
    }
  }, [tokenExpired]);

  useEffect(() => {
    if (!calendarIntegrationSettings && hasMs365CalendarIntegration) {
      wrapThunkAction(
        dispatch(
          getCalendarIntegrationStatus({
            programId: mentoringProgramId,
            enrollmentId,
          }),
        ),
      );
    }
  }, [mentoringProgramId, enrollmentId]);

  useEffect(() => {
    if (mentoringProgramId) {
      dispatch(getMentorshipProgramGuides({ mentoringProgramId }));
    }
  }, [mentoringProgramId]);

  useEffect(() => {
    const parent = document.getElementById('mentoring-program-root');
    if (parent) {
      parent.style.zIndex = showPlanSessionFlyoutModal ? '1026' : 'unset';
    }
  }, [showPlanSessionFlyoutModal]);

  useEffect(() => {
    if (mentoringProgramId && connectionId) {
      dispatch(getMentoringProgramReflectionResponses({ programId: mentoringProgramId, connectionId }));
    }
  }, [mentoringProgramId, connectionId]);

  useEffect(() => {
    dispatch(
      getMentoringProgramSessions({
        programId: mentoringProgramId,
        connectionId,
        filter: SessionFilters.PAST,
      }),
    );
  }, []);

  return (
    <div css={getSanitizedStyles([
      styles(
        containerTranslate,
        isTablet,
        (!isFeatureReleased || isProgramEnded),
        showProgramReflectionBanner,
      ),
      unreleasedFeatureStyles,
    ])}
    >
      {isFeatureReleased && !isProgramEnded && (
        <>
          <div className='participant-home'>
            <div className='d-flex flex-column left-panel'>
              {(showProgramReflectionBanner && isTablet) && <ProgramReflectionBanner />}
              {connectionId && (
                <NextStepsPanel
                  programId={mentoringProgram?.id}
                  connectionId={connectionId}
                  enrollmentId={mentorshipProgramEnrollment}
                  openSessionPulseCheck={openSessionPulseCheck}
                  showPlanSessionFlyout={openPlanSessionFlyout}
                />
              )}
              {!areAllStepsCompleted && <StepList showPlanSessionFlyout={openPlanSessionFlyout} />}
              {(isWaitingFirstSessionCompleted && isSessionCreated) ? (
                <SessionsList showPlanSessionFlyout={openPlanSessionFlyout}>
                  <>
                    {isMentor && connectionId && mentoringProgramId && aiMeetingPrepEnabled && hasAiComponentGeneration && (
                      <MenteeLensPanel
                        programId={mentoringProgramId}
                        connectionId={connectionId}
                      />
                    )}
                    {actionItems}
                  </>
                </SessionsList>
              ) : actionItems}
              {shouldShowConnectionContent && <GoalsList />}
              {!shouldShowConnectionContent && <ProgramOrientation />}
            </div>
            <div className='d-flex flex-column right-panel panel-container'>
              {(showProgramReflectionBanner && !isTablet) && <ProgramReflectionBanner />}
              <ConnectionDetails />
              {shouldShowConnectionContent && <ProgramOrientation />}
            </div>
          </div>
          {(hasSingleConnection || isParticipantDrivenMatching) && <MatchCelebration />}
        </>
      )}
      {!isFeatureReleased && (
        <UnreleasedFeature date={releasedDate} />
      )}
      {isProgramEnded && (
        <ProgramClosed />
      )}
      {showPlanSessionFlyoutModal && (
        <PlanSessionFlyoutModal
          ref={editModeRef}
          session={sessionToEdit}
          onClose={() => {
            setSessionToEdit(null);
            setShowPlanSessionFlyoutModal(false);
            if (newSession) {
              resetNewSessionParam();
            }
          }}
          editMode={editMode}
          createMeetingPlan={createMeetingPlan}
          integrations={integrations}
          shouldShowPulseCheckCTA={shouldShowPulseCheckCTA}
          openSessionPulseCheck={openSessionPulseCheck}
          openIntegrationsModal={() => setShowIntegrationsModal(true)}
        />
      )}
      {showProfileModal && (
        <ProfileModal
          title={showProfile && t.MENTORING_PROGRAMS.PARTICIPANT_HOME.GET_STARTED.PROFILE_MODAL.TITLE.DEFAULT()}
          setShowModal={setShowProfileModal}
        />
      )}
      {showPulseCheckModal && pulseChecksEnabled && (
        <PulseCheckModal
          sessionId={sessionId}
          setShowModal={setShowPulseCheckModal}
          setShouldShowPulseCheckCTA={setShouldShowPulseCheckCTA}
        />
      )}
      {showIntegrationsModal && (
        <EnableIntegrationsModal
          session={editModeRef.current?.getSession()}
          sessionId={sessionToEdit?.id}
          integrations={integrations}
          createMeetingPlan={createMeetingPlan}
          onClose={() => setShowIntegrationsModal(false)}
        />
      )}
      {showMentorRequestModal && <MentorRequestModal />}
      {guidanceEnabled && mentoringProgramGuide && (
        <GuideModal
          mentoringProgramGuide={mentoringProgramGuide}
        />
      )}
      {programReflectionEnabled && showReflections && mentoringProgramId && connectionId && (
        <ReflectionWorkspaceContainer
          programId={mentoringProgramId}
          connectionId={connectionId}
          programName={mentoringProgram?.name}
        />
      )}
    </div>
  );
};

export default ParticipantHome;
